import {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Papa from 'papaparse';
import { AlertTriangle, Loader2 } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { getPresignedDownloadUrl } from '@/services/data-sources/get-presigned-download-url';

import type { PreviewColumn } from './csv-preview-grid';

const CsvPreviewGrid = lazy(() => import('./csv-preview-grid'));

interface CsvPreviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orgId: string;
  collectionId: string;
  s3Key: string | null;
  filename?: string;
}

interface ColumnInfo extends PreviewColumn {
  fieldName: string;
}

const CHUNK_SIZE = 256 * 1024; // 256 KB per request
const MAX_ROWS = 1_000_000;

const CsvPreviewDialog = ({
  open,
  onOpenChange,
  orgId,
  collectionId,
  s3Key,
  filename,
}: CsvPreviewDialogProps) => {
  const [presignedUrl, setPresignedUrl] = useState<string | null>(null);
  const [columnsInfo, setColumnsInfo] = useState<ColumnInfo[]>([]);
  const [rows, setRows] = useState<Record<string, string | null>[]>([]);
  const [initialLoading, setInitialLoading] = useState(false);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);

  const offsetRef = useRef(0);
  const partialLineRef = useRef('');
  const totalSizeRef = useRef<number | null>(null);
  const headerFieldsRef = useRef<string[]>([]);
  const columnsInfoRef = useRef<ColumnInfo[]>([]);
  const rangeSupportedRef = useRef(true);
  const fallbackLoadedRef = useRef(false);
  const rowsRef = useRef<Record<string, string | null>[]>([]);

  useEffect(() => {
    columnsInfoRef.current = columnsInfo;
  }, [columnsInfo]);

  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  const resetState = useCallback(() => {
    setPresignedUrl(null);
    setColumnsInfo([]);
    setRows([]);
    setInitialLoading(false);
    setIsFetchingMore(false);
    setError(null);
    setHasMore(false);
    offsetRef.current = 0;
    partialLineRef.current = '';
    totalSizeRef.current = null;
    headerFieldsRef.current = [];
    columnsInfoRef.current = [];
    rangeSupportedRef.current = true;
    fallbackLoadedRef.current = false;
  }, []);

  useEffect(() => {
    if (!open) {
      resetState();
      return;
    }

    if (!s3Key || !orgId || !collectionId) {
      setError('Missing dataset context for CSV preview.');
      return;
    }

    resetState();
    setInitialLoading(true);

    void (async () => {
      try {
        const targetFilename = filename ?? s3Key.split('/').pop();
        if (!targetFilename) {
          throw new Error('Unable to determine filename for preview.');
        }

        const presigned = await getPresignedDownloadUrl({
          orgId,
          collectionId,
          filename: targetFilename,
          download: false,
          expiresIn: 300,
        });

        if (!presigned || 'detail' in presigned) {
          throw new Error(
            presigned?.detail || 'Failed to generate preview link.',
          );
        }

        setPresignedUrl(presigned.presigned_url);
        setHasMore(true);
      } catch (fetchError) {
        console.error(
          'Failed to fetch presigned URL for CSV preview:',
          fetchError,
        );
        const message =
          fetchError instanceof Error
            ? fetchError.message
            : 'Failed to fetch CSV preview.';
        setError(message);
      } finally {
        setInitialLoading(false);
      }
    })();
  }, [collectionId, filename, open, orgId, resetState, s3Key]);

  const mapRowWithHeader = useCallback(
    (row: Record<string, string | null>, info: ColumnInfo[]) => {
      const mapped: Record<string, string | null> = {};
      info.forEach((column) => {
        const value = row[column.fieldName] ?? null;
        mapped[column.key] = value === '' ? null : value;
      });
      return mapped;
    },
    [],
  );

  const mapRowFromArray = useCallback((row: string[]) => {
    const mapped: Record<string, string | null> = {};
    columnsInfoRef.current.forEach((column, index) => {
      const value = row[index] ?? null;
      mapped[column.key] = value === '' ? null : value;
    });
    return mapped;
  }, []);

  const parseChunk = useCallback(
    (chunk: string, isFirstChunk: boolean) => {
      if (!chunk.trim()) return [] as Record<string, string | null>[];

      if (isFirstChunk) {
        const parsed = Papa.parse<Record<string, string | null>>(chunk, {
          header: true,
          skipEmptyLines: true,
        });
        const fields = parsed.meta.fields || [];
        headerFieldsRef.current = fields;
        const info = fields.map((field: string | undefined, index: number) => ({
          fieldName: field ?? `Column ${index + 1}`,
          key: `col_${index}`,
          title: field && field.trim().length ? field : `Column ${index + 1}`,
        }));
        columnsInfoRef.current = info;
        setColumnsInfo(info);
        const newRows = (parsed.data || []).map(
          (row: Record<string, string | null>) => mapRowWithHeader(row, info),
        );
        return newRows;
      }

      const parsed = Papa.parse<string[]>(chunk, {
        header: false,
        skipEmptyLines: true,
      });
      const data = (parsed.data || []) as string[][];
      return data.map((row) => mapRowFromArray(row));
    },
    [mapRowFromArray, mapRowWithHeader],
  );

  const fetchNextChunk = useCallback(
    async (initial = false) => {
      if (!presignedUrl || !hasMore) return;
      if (!initial && rowsRef.current.length >= MAX_ROWS) {
        setHasMore(false);
        return;
      }
      if (!initial && isFetchingMore) return;

      setIsFetchingMore(true);

      try {
        console.log('csv-preview-dialog fetch chunk', {
          initial,
          offset: offsetRef.current,
        });
        const start = offsetRef.current;
        const end = start + CHUNK_SIZE - 1;
        const response = await fetch(presignedUrl, {
          headers: {
            Range: `bytes=${start}-${end}`,
          },
        });

        if (!response.ok && response.status !== 206) {
          throw new Error('Failed to fetch CSV preview chunk.');
        }

        const contentRange = response.headers.get('Content-Range');
        let isLastChunk = false;

        if (contentRange) {
          const match = /bytes\s+(\d+)-(\d+)\/(\d+)/.exec(contentRange);
          if (match) {
            const [, , rangeEnd, total] = match;
            const endIndex = Number(rangeEnd);
            const totalSize = Number(total);
            totalSizeRef.current = totalSize;
            offsetRef.current = endIndex + 1;
            if (endIndex + 1 >= totalSize) {
              isLastChunk = true;
            }
          }
        } else {
          // Whole file returned
          isLastChunk = true;
        }

        const text = await response.text();
        const combined = `${partialLineRef.current}${text}`;
        let adjusted = combined;

        if (!isLastChunk) {
          const lastNewline = Math.max(
            combined.lastIndexOf('\n'),
            combined.lastIndexOf('\r'),
          );
          if (lastNewline !== -1 && lastNewline < combined.length - 1) {
            partialLineRef.current = combined.slice(lastNewline + 1);
            adjusted = combined.slice(0, lastNewline + 1);
          } else if (lastNewline === -1) {
            // No newline found, keep accumulating until we have a full line
            partialLineRef.current = combined;
            adjusted = '';
          } else {
            partialLineRef.current = '';
          }
        } else {
          partialLineRef.current = '';
        }

        if (adjusted.trim().length === 0) {
          setHasMore(!isLastChunk);
          setIsFetchingMore(false);
          return;
        }

        const appendRows = parseChunk(
          adjusted,
          headerFieldsRef.current.length === 0,
        );
        if (appendRows.length) {
          const nextLengthEstimate = Math.min(
            rowsRef.current.length + appendRows.length,
            MAX_ROWS,
          );
          setRows((prev) => [...prev, ...appendRows].slice(0, MAX_ROWS));
          const canLoadMore = !isLastChunk && nextLengthEstimate < MAX_ROWS;
          setHasMore(canLoadMore);
        } else {
          setHasMore(!isLastChunk && rowsRef.current.length < MAX_ROWS);
        }
      } catch (fetchError) {
        console.error('Failed to load CSV preview chunk:', fetchError);
        const message =
          fetchError instanceof Error
            ? fetchError.message
            : 'Failed to load CSV preview chunk.';
        setError(message);
        setHasMore(false);
      } finally {
        setIsFetchingMore(false);
      }
    },
    [hasMore, isFetchingMore, parseChunk, presignedUrl],
  );

  useEffect(() => {
    if (!open || !presignedUrl) return;
    if (rows.length === 0 && !isFetchingMore) {
      void fetchNextChunk(true);
    }
  }, [fetchNextChunk, isFetchingMore, open, presignedUrl, rows.length]);

  const handleScroll: React.UIEventHandler<HTMLDivElement> = useCallback(
    (event) => {
      const target = event.currentTarget;
      if (!target) return;
      const threshold = 200;
      if (hasMore && !isFetchingMore) {
        const remaining =
          target.scrollHeight - target.scrollTop - target.clientHeight;
        if (remaining < threshold) {
          void fetchNextChunk();
        }
      }
    },
    [fetchNextChunk, hasMore, isFetchingMore],
  );

  const previewColumns: PreviewColumn[] = useMemo(
    () => columnsInfo.map(({ key, title }) => ({ key, title })),
    [columnsInfo],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='w-[80vw] max-w-[80vw] sm:max-w-[80vw]'>
        <DialogHeader>
          <DialogTitle>CSV preview</DialogTitle>
          {filename && <DialogDescription>{filename}</DialogDescription>}
        </DialogHeader>

        {initialLoading && (
          <div className='flex h-64 items-center justify-center'>
            <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
          </div>
        )}

        {!initialLoading && error && (
          <div className='flex flex-col gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive'>
            <div className='flex items-start gap-2'>
              <AlertTriangle className='h-4 w-4' />
              <p>{error}</p>
            </div>
            {presignedUrl && (
              <Button
                type='button'
                variant='outline'
                size='sm'
                className='self-start'
                onClick={() => {
                  setError(null);
                  void fetchNextChunk(true);
                }}
              >
                Retry
              </Button>
            )}
          </div>
        )}

        {!initialLoading && !error && (
          <div className='flex flex-col gap-3'>
            <div className='border rounded-md overflow-hidden'>
              <div className='h-[60vh] overflow-auto' onScroll={handleScroll}>
                <Suspense
                  fallback={
                    <div className='flex h-full items-center justify-center'>
                      <Loader2 className='h-5 w-5 animate-spin text-muted-foreground' />
                    </div>
                  }
                >
                  <CsvPreviewGrid
                    className='min-h-full'
                    columns={previewColumns}
                    rows={rows}
                  />
                </Suspense>
              </div>
            </div>
            <div className='flex items-center justify-between text-xs text-muted-foreground'>
              <span>
                Showing {rows.length.toLocaleString()} row
                {rows.length === 1 ? '' : 's'}
              </span>
              {isFetchingMore && (
                <div className='flex items-center gap-2 text-muted-foreground'>
                  <Loader2 className='h-4 w-4 animate-spin' />
                  <span>Loading more…</span>
                </div>
              )}
              {!hasMore && rows.length >= MAX_ROWS && (
                <span>
                  Preview limited to first {MAX_ROWS.toLocaleString()} rows.
                </span>
              )}
              {!hasMore &&
                !isFetchingMore &&
                rows.length > 0 &&
                rows.length < MAX_ROWS && <span>End of file</span>}
              {hasMore && !isFetchingMore && (
                <Button
                  type='button'
                  variant='outline'
                  size='sm'
                  onClick={() => fetchNextChunk()}
                >
                  Load more
                </Button>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CsvPreviewDialog;
